import { useEffect, useRef, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './store';

import LoadingScreen from './shared/components/LoadingScreen';
import StartGameOverlay from './features/auth/components/StartGameOverlay';
import AuthErrorModal from './features/auth/components/AuthErrorModal';
import DesktopQRScreen from './features/auth/components/DesktopQRScreen';
import { AppRoutes } from './AppRoutes';
import { AppProviders } from './AppProviders';
import { useAuth } from './features/auth/hooks/useAuth';
import { useUserRedux } from './hooks/useUserRedux';
import { useRenderLogger } from './shared/hooks/useRenderLogger';
import { isTelegramWebApp } from './shared/utils/platform';
import logger from './core/logger/logger';
import TonPaymentStatusModal from './components/TonPaymentStatusModal';
import { clearTonPayment } from './store/slices/tonPaymentSlice';
import EarlyAccessPage from './features/shop/pages/EarlyAccessPage';
import LegalConsentModal from './features/legal/components/LegalConsentModal';
import { fetchLegalStatus, selectLegalConsentRequired } from './features/legal/store/legalSlice';
import { LEGAL_REQUIRED_EVENT } from './features/legal/utils/legalEvents';
import { useTranslation } from 'react-i18next';
import { initI18n } from './i18n';
import { useLanguageSync } from './i18n/useLanguageSync';

// Файл языка ждём не дольше этого — дальше показываем английские дефолты
const I18N_WAIT_MS = 5000;

/**
 * Корневой компонент: маршрутизация по платформе.
 * В обычном браузере (без Telegram) показываем QR-экран без запуска авторизации;
 * в любом клиенте Telegram (мобильный, Desktop, macOS, Web) — основное приложение.
 */
function AppContent() {
  if (!isTelegramWebApp()) {
    return <DesktopQRScreen />;
  }
  return <MobileAppContent />;
}

/**
 * Главный компонент приложения
 *
 * Поток:
 * 1. Spine-заставка играет пока идёт авторизация
 * 2. После успешной авторизации — поверх заставки появляется StartGameOverlay
 * 3. Для вернувшихся пользователей: кнопка Early Access открывает страницу Early Access
 *    (поверх загрузки, без старта игры — возможна покупка паков)
 * 4. По нажатию Start Game / Close в Early Access — заставка скрывается, рендерится AppRoutes
 * 5. При ошибке авторизации — поверх заставки показывается AuthErrorModal с кнопкой Retry
 * 6. Пока не приняты все активные юр. документы (политика ПД, оферта, партнёрка) — вместо Start Game
 *    окно согласия LegalConsentModal; оно же открывается поверх игры, если покупка вернула 403
 *    LEGAL_ACCEPTANCE_REQUIRED (документ получил новую версию)
 */
function MobileAppContent() {
  const balanceRef = useRef();
  const [gameStarted, setGameStarted] = useState(false);
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setI18nReady(true), I18N_WAIT_MS);
    initI18n().finally(() => {
      clearTimeout(timer);
      setI18nReady(true);
    });
    return () => clearTimeout(timer);
  }, []);

  const { loading: authLoading, error: authError, retry: retryAuth } = useAuth();
  const { data: user } = useUserRedux();
  const tonPayment = useSelector((state) => state.tonPayment);
  const legalRequired = useSelector(selectLegalConsentRequired);
  // Статус загружен (или запрос упал — не блокируем вход, покупки сервер всё равно не пропустит)
  const legalChecked = useSelector((state) => state.legal.allAccepted !== null || !!state.legal.error);

  const authReady = !authLoading && i18nReady;
  const authSuccess = authReady && !authError && !!user?.id;
  const authFailed = authReady && (authError || (!user?.id));

  useLanguageSync(authSuccess);

  useEffect(() => {
    if (authSuccess) dispatch(fetchLegalStatus());
  }, [authSuccess, dispatch]);

  useEffect(() => {
    const onLegalRequired = () => dispatch(fetchLegalStatus());
    window.addEventListener(LEGAL_REQUIRED_EVENT, onLegalRequired);
    return () => window.removeEventListener(LEGAL_REQUIRED_EVENT, onLegalRequired);
  }, [dispatch]);

  const handleRewardComplete = () => {
    logger.info('Daily reward completed');
  };

  const handleStartGame = () => {
    setEarlyAccessOpen(false);
    setGameStarted(true);
  };

  useRenderLogger('MobileAppContent', {
    authLoading,
    authError: !!authError,
    userId: user?.id,
    gameStarted,
  });

  // AppProviders нужен и для Early Access (покупки паков), и для основной игры
  const needProviders = authSuccess && (earlyAccessOpen || gameStarted);

  return (
    <div className="app-shell">
      {/* Landscape orientation blocker */}
      <div className="rotate-overlay">
        <img src="/logo.png" alt="Datum Empire" className="rotate-logo" />
        <div className="rotate-phone-wrap">
          {/* Phone outline: portrait orientation, CSS rotates it landscape→portrait */}
          <svg className="rotate-phone-svg" viewBox="0 0 56 92" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="52" height="88" rx="9" stroke="currentColor" strokeWidth="3.5"/>
            <circle cx="28" cy="13" r="3" fill="currentColor" opacity="0.45"/>
            <rect x="19" y="79" width="18" height="5" rx="2.5" fill="currentColor" opacity="0.45"/>
          </svg>
          {/* Rotation arc — counterclockwise arrow hinting at the action */}
          <svg className="rotate-arc-svg" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M 80 50 A 30 30 0 1 0 50 80" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <polyline points="44,73 50,80 56,73" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <p className="rotate-title">{t('app.rotate.title', 'Rotate your device')}</p>
        <p className="rotate-hint">{t('app.rotate.hint', 'This game is portrait only')}</p>
      </div>

      {/* Spine-анимация заставки — скрывается когда открыт Early Access или игра стартовала */}
      {!gameStarted && !earlyAccessOpen && <LoadingScreen />}

      {/* Оверлей Start Game — виден пока не открыт Early Access и игра не стартовала */}
      {!gameStarted && !earlyAccessOpen && authSuccess && legalChecked && !legalRequired && (
        <StartGameOverlay
          onStart={handleStartGame}
          onEarlyAccess={() => setEarlyAccessOpen(true)}
          user={user}
        />
      )}

      {/* Ошибка авторизации */}
      {!gameStarted && !earlyAccessOpen && authFailed && (
        <AuthErrorModal onRetry={retryAuth} />
      )}

      {/* Единый AppProviders для Early Access и основной игры */}
      {needProviders && user?.id && (
        <AppProviders userId={user.id}>
          {/* Early Access — показывается до старта игры, поверх загрузочного экрана */}
          {earlyAccessOpen && !gameStarted && (
            <EarlyAccessPage onClose={handleStartGame} />
          )}

          {/* Основное приложение — рендерится после старта */}
          {gameStarted && (
            <div className="app-content">
              <AppRoutes
                balanceRef={balanceRef}
                onRewardComplete={handleRewardComplete}
              />
            </div>
          )}
        </AppProviders>
      )}

      {/* Согласие с политикой ПД / офертой / партнёркой — первый вход и новые версии документов */}
      {authSuccess && legalRequired && <LegalConsentModal />}

      {/* Глобальный статус TON-оплаты */}
      {tonPayment.isOpen && (
        <TonPaymentStatusModal
          status={tonPayment.status}
          item={tonPayment.item}
          tonAmount={tonPayment.tonAmount}
          error={tonPayment.error}
          onClose={() => dispatch(clearTonPayment())}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
